import { useState, useEffect } from 'react';
import { Download, X, Share, PlusSquare, Monitor, Smartphone } from 'lucide-react';
import { Button } from '../components/ui/button';

function getDeviceInfo() {
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
  return { isIOS, isAndroid, isSafari, isStandalone };
}

export default function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [device, setDevice] = useState({});
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSSteps, setShowIOSSteps] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem('euromoto_install_dismissed_v2');
    if (dismissed) return;

    const info = getDeviceInfo();
    setDevice(info);

    if (info.isStandalone) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    const timer = setTimeout(() => setVisible(true), 3000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem('euromoto_install_dismissed_v2', Date.now().toString());
  };

  const handleDesktopInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-[55]" data-testid="install-prompt">
      <div className="bg-[#141B2D] border border-[#FADB5F]/30 rounded-2xl p-5 shadow-2xl shadow-black/50 backdrop-blur-xl">
        <button onClick={dismiss} className="absolute top-3 right-3 text-[#475569] hover:text-[#94A3B8]" data-testid="install-dismiss-btn">
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <img src="/euromoto_icon_512.png" alt="EuroMoto" className="w-12 h-12 rounded-xl" />
          <div>
            <h3 className="text-sm font-bold text-[#F1F5F9]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              EuroMoto.Online
            </h3>
            <p className="text-xs text-[#64748B]">v1.0.0 • Free</p>
          </div>
        </div>

        {/* iOS */}
        {device.isIOS && (
          <div data-testid="install-ios">
            {!showIOSSteps ? (
              <>
                <p className="text-xs text-[#94A3B8] mb-3">
                  Инсталирайте приложението на вашия iPhone / iPad
                </p>
                <Button
                  onClick={() => setShowIOSSteps(true)}
                  size="sm"
                  className="w-full bg-[#FADB5F] text-[#0F172A] hover:bg-[#E5C34A] font-semibold text-xs h-10"
                  data-testid="install-ios-btn"
                >
                  <Smartphone className="w-4 h-4 mr-2" />
                  Как да инсталирам?
                </Button>
              </>
            ) : (
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-2.5 rounded-lg bg-white/5">
                  <div className="w-6 h-6 rounded-full bg-[#FADB5F] text-[#0F172A] flex items-center justify-center text-xs font-bold shrink-0">1</div>
                  <div>
                    <p className="text-xs text-[#F1F5F9] font-medium">Отворете в Safari</p>
                    <p className="text-[10px] text-[#64748B]">Работи само в Safari браузъра</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-2.5 rounded-lg bg-white/5">
                  <div className="w-6 h-6 rounded-full bg-[#FADB5F] text-[#0F172A] flex items-center justify-center text-xs font-bold shrink-0">2</div>
                  <div>
                    <p className="text-xs text-[#F1F5F9] font-medium flex items-center gap-1">
                      Натиснете <Share className="w-3.5 h-3.5 text-[#FADB5F]" /> Share бутона
                    </p>
                    <p className="text-[10px] text-[#64748B]">Долу в средата на екрана</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-2.5 rounded-lg bg-white/5">
                  <div className="w-6 h-6 rounded-full bg-[#FADB5F] text-[#0F172A] flex items-center justify-center text-xs font-bold shrink-0">3</div>
                  <div>
                    <p className="text-xs text-[#F1F5F9] font-medium flex items-center gap-1">
                      Изберете <PlusSquare className="w-3.5 h-3.5 text-[#FADB5F]" /> Add to Home Screen
                    </p>
                    <p className="text-[10px] text-[#64748B]">Скролнете надолу ако не виждате</p>
                  </div>
                </div>
                <div className="text-center pt-1">
                  <button onClick={dismiss} className="text-xs text-[#64748B] hover:text-[#94A3B8]">Разбрах, затвори</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Android */}
        {device.isAndroid && (
          <div data-testid="install-android">
            <p className="text-xs text-[#94A3B8] mb-3">
              Свалете приложението за бърз достъп до обяви в цяла Европа
            </p>
            <a
              href="/EuroMoto.apk"
              download="EuroMoto.apk"
              onClick={dismiss}
              className="flex items-center justify-center gap-2 w-full h-10 rounded-lg bg-[#FADB5F] text-[#0F172A] font-semibold text-xs hover:bg-[#E5C34A] transition-colors"
              data-testid="install-android-btn"
            >
              <Download className="w-4 h-4" />
              Свали APK (936 KB)
            </a>
          </div>
        )}

        {/* Desktop */}
        {!device.isIOS && !device.isAndroid && (
          <div data-testid="install-desktop">
            <p className="text-xs text-[#94A3B8] mb-3">
              Инсталирайте приложението на вашия компютър за бърз достъп
            </p>
            {deferredPrompt ? (
              <Button
                onClick={handleDesktopInstall}
                size="sm"
                className="w-full bg-[#FADB5F] text-[#0F172A] hover:bg-[#E5C34A] font-semibold text-xs h-10"
                data-testid="install-desktop-btn"
              >
                <Monitor className="w-4 h-4 mr-2" />
                Инсталирай приложението
              </Button>
            ) : (
              <div className="p-2.5 rounded-lg bg-white/5">
                <p className="text-xs text-[#F1F5F9] text-center">
                  Натиснете <span className="text-[#FADB5F] font-semibold">иконката за инсталиране</span> в адресната лента на браузъра
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
